import React, { useState, useCallback, useRef, memo } from "react";
import { Link } from "react-router-dom";
import "isomorphic-fetch";
import mapImg from "../../assets/images/map2.png";
import { useForm } from "../../hooks/useForm";
import { useApi } from "../../hooks/useApi";
import SkeletonLoader from "../Common/SkeletonLoader";

const Contact = () => {
  const [submitted, setSubmitted] = useState(false);
  const [buttonState, setButtonState] = useState("");
  const submittingRef = useRef(false);

  const {
    values: formFields,
    errors,
    isSubmitting,
    setIsSubmitting,
    handleChange,
    reset,
    validate,
  } = useForm({
    name: "",
    email: "",
    subject: "",
    phone: "",
    text: "",
  });

  const { execute: submitForm } = useApi();

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      // Sync guard — blocks double-clicks before React re-renders
      if (submittingRef.current) return;

      // Validation rules
      const validationRules = {
        name: { required: true, message: "Name is required" },
        email: {
          required: true,
          pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address",
        },
        subject: { required: true, message: "Subject is required" },
        text: { required: true, message: "Message is required" },
      };

      if (!validate(validationRules)) {
        setButtonState("Please fill in all required fields correctly");
        setTimeout(() => setButtonState(""), 3000);
        return;
      }

      submittingRef.current = true;
      setIsSubmitting(true);
      setButtonState("Submitting...");

      try {
        await submitForm(async () => {
          const response = await fetch(
            "https://wcr6mtxmgboe76wv2d6vps4jh40fwlkv.lambda-url.ap-northeast-3.on.aws/api/email",
            {
              method: "post",
              mode: "cors",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ body: JSON.stringify(formFields) }),
            }
          );

          if (response.status === 200) {
            setSubmitted(true);
            setButtonState("");
            reset();
          } else {
            setButtonState("Error occurred. Please try again.");
            setTimeout(() => setButtonState(""), 3000);
          }
        });
      } catch (error) {
        console.error("Contact form error:", error);
        setButtonState("Network error. Please try again.");
        setTimeout(() => setButtonState(""), 3000);
      } finally {
        submittingRef.current = false;
        setIsSubmitting(false);
      }
    },
    [formFields, validate, submitForm, reset, setIsSubmitting]
  );

  const onHideSuccess = useCallback(() => {
    setSubmitted(false);
  }, []);

  const successMessage = () => {
    if (submitted) {
      return (
        <div className="alert-success" uk-alert-success>
          <Link
            to="#"
            onClick={onHideSuccess}
            className="uk-alert-close"
            uk-close="true"
          ></Link>
          <h3>Thank you</h3>
          <p>We will connect you soon.</p>
        </div>
      );
    }
  };
  return (
    <>
      <section id="contact" className="contact-area uk-contact uk-section">
        <div className="uk-container">
          <div className="uk-section-title section-title">
            <span>Let's Talk</span>
            <h2>Get in Touch</h2>
          </div>

          <div className="uk-grid uk-grid-match uk-grid-medium uk-child-width-1-2@m uk-child-width-1-1@s">
            <div className="item">
              <div className="map-img">
                <img src={mapImg} alt="map" />

                {/* <div className="location uk-location1">
                  <Link to="#" className="active">
                    <div className="location-info">
                      <h5>兵庫県</h5>
                      <span>芦屋市精道町４−１５−３F</span>
                    </div>
                  </Link>
                </div> */}
              </div>
            </div>

            <div className="item">
              <form id="contactForm" onSubmit={onSubmit}>
                <div className="uk-grid uk-grid-match uk-grid-medium uk-child-width-1-2@m uk-child-width-1-1@s">
                  <div className="item uk-margin">
                    <input
                      type="text"
                      className={`uk-input ${
                        errors.name ? "uk-form-danger" : ""
                      }`}
                      name="name"
                      id="name"
                      placeholder="Name"
                      value={formFields.name}
                      onChange={handleChange("name")}
                      required={true}
                    />
                    {errors.name && (
                      <div className="uk-text-danger uk-text-small">
                        {errors.name}
                      </div>
                    )}
                  </div>

                  <div className="item uk-margin">
                    <input
                      type="email"
                      className={`uk-input ${
                        errors.email ? "uk-form-danger" : ""
                      }`}
                      name="email"
                      id="email"
                      placeholder="Email"
                      value={formFields.email}
                      onChange={handleChange("email")}
                      required={true}
                    />
                    {errors.email && (
                      <div className="uk-text-danger uk-text-small">
                        {errors.email}
                      </div>
                    )}
                  </div>

                  <div className="item uk-margin">
                    <input
                      type="text"
                      className="uk-input"
                      placeholder="Phone"
                      value={formFields.phone}
                      onChange={handleChange("phone")}
                    />
                  </div>

                  <div className="item uk-margin">
                    <input
                      type="text"
                      className={`uk-input ${
                        errors.subject ? "uk-form-danger" : ""
                      }`}
                      name="subject"
                      id="subject"
                      placeholder="Subject"
                      value={formFields.subject}
                      onChange={handleChange("subject")}
                      required={true}
                    />
                    {errors.subject && (
                      <div className="uk-text-danger uk-text-small">
                        {errors.subject}
                      </div>
                    )}
                  </div>
                </div>

                <div className="item">
                  <textarea
                    name="message"
                    className={`uk-textarea ${
                      errors.text ? "uk-form-danger" : ""
                    }`}
                    id="message"
                    cols="30"
                    rows="4"
                    placeholder="Your Message"
                    value={formFields.text}
                    onChange={handleChange("text")}
                    required={true}
                  />
                  {errors.text && (
                    <div className="uk-text-danger uk-text-small">
                      {errors.text}
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  className={`uk-button uk-button-default ${
                    isSubmitting ? "loading" : ""
                  }`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <SkeletonLoader
                        width="20px"
                        height="20px"
                        variant="circular"
                      />
                      <span style={{ marginLeft: "8px" }}>Submitting...</span>
                    </>
                  ) : (
                    buttonState || "Submit Message"
                  )}
                </button>
              </form>
              {successMessage()}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default memo(Contact);
